import { DateTime } from 'luxon';
import type {
  AnnualHolidayRule,
  FloatingAnnualHolidayRule,
  LuxonWeekday,
} from '@act/shared/types';
import { REEMA_OBSERVED_HOLIDAYS } from '@act/shared/constants';

export type {
  AnnualHolidayRule,
  FixedAnnualHolidayRule,
  FloatingAnnualHolidayRule,
  FridayAfterThanksgivingHolidayRule,
  LuxonWeekday,
} from '@act/shared/types';

/**
 * The calendar date of the Nth given weekday in a month (e.g. 3rd Monday).
 * Returns null if that occurrence does not exist (e.g. 5th Monday when there are only four).
 */
export function getDateOfNthWeekdayInMonth(
  year: number,
  month: number,
  targetWeekday: LuxonWeekday,
  occurrenceIndex: number,
  timeZone: string
): DateTime | null {
  if (occurrenceIndex < 1 || occurrenceIndex > 5) {
    return null;
  }
  const firstDayOfMonth = DateTime.fromObject(
    { year, month, day: 1 },
    { zone: timeZone }
  ).startOf('day');
  if (!firstDayOfMonth.isValid) {
    return null;
  }
  const daysAfterFirstUntilTargetWeekday =
    (targetWeekday - firstDayOfMonth.weekday + 7) % 7;
  const nthWeekdayDate = firstDayOfMonth
    .plus({ days: daysAfterFirstUntilTargetWeekday })
    .plus({ weeks: occurrenceIndex - 1 });
  if (nthWeekdayDate.month !== month) {
    return null;
  }
  return nthWeekdayDate;
}

/**
 * The last occurrence of `targetWeekday` in `month` (e.g. last Monday in May for Memorial Day).
 */
export function getDateOfLastWeekdayInMonth(
  year: number,
  month: number,
  targetWeekday: LuxonWeekday,
  timeZone: string
): DateTime | null {
  const firstDayOfMonth = DateTime.fromObject(
    { year, month, day: 1 },
    { zone: timeZone }
  ).startOf('day');
  if (!firstDayOfMonth.isValid) {
    return null;
  }
  const lastCalendarDay = firstDayOfMonth.endOf('month').startOf('day');
  let cursor = lastCalendarDay;
  for (let i = 0; i < 7; i++) {
    if (cursor.weekday === targetWeekday) {
      return cursor;
    }
    cursor = cursor.minus({ days: 1 });
  }
  return null;
}

function getFloatingWeekdayDateInMonth(
  year: number,
  month: number,
  weekday: LuxonWeekday,
  occurrence: FloatingAnnualHolidayRule['occurrence'],
  timeZone: string
): DateTime | null {
  if (occurrence === 'last') {
    return getDateOfLastWeekdayInMonth(year, month, weekday, timeZone);
  }
  return getDateOfNthWeekdayInMonth(year, month, weekday, occurrence, timeZone);
}

function getFridayAfterUsThanksgiving(
  year: number,
  timeZone: string
): DateTime | null {
  const thanksgiving = getDateOfNthWeekdayInMonth(year, 11, 4, 4, timeZone);
  if (!thanksgiving) {
    return null;
  }
  const friday = thanksgiving.plus({ days: 1 });
  return friday.weekday === 5 ? friday : null;
}

// If holiday falls on a weekend, move it to the nearest business day
export function applyWeekendHolidayObservance(
  holidayDate: DateTime,
  timeZone: string
): DateTime {
  const dateInZone = holidayDate.setZone(timeZone).startOf('day');
  const weekday = dateInZone.weekday;
  if (weekday === 6) {
    return dateInZone.minus({ days: 1 });
  }
  if (weekday === 7) {
    return dateInZone.plus({ days: 1 });
  }
  return dateInZone;
}

/**
 * The observed (bank) date for one rule in a given calendar year in `timeZone`.
 * Returns null if the rule does not resolve (invalid fixed date or missing nth weekday).
 */
export function getObservedHolidayDateForYear(
  year: number,
  rule: AnnualHolidayRule,
  timeZone: string
): DateTime | null {
  if (rule.kind === 'fixed') {
    const fixedCalendarDate = DateTime.fromObject(
      { year, month: rule.month, day: rule.day },
      { zone: timeZone }
    ).startOf('day');
    if (!fixedCalendarDate.isValid) {
      return null;
    }
    return applyWeekendHolidayObservance(fixedCalendarDate, timeZone);
  }
  if (rule.kind === 'fridayAfterThanksgiving') {
    const fridayDate = getFridayAfterUsThanksgiving(year, timeZone);
    if (!fridayDate) {
      return null;
    }
    return applyWeekendHolidayObservance(fridayDate, timeZone);
  }
  const floatingCalendarDate = getFloatingWeekdayDateInMonth(
    year,
    rule.month,
    rule.weekday,
    rule.occurrence,
    timeZone
  );
  if (!floatingCalendarDate) {
    return null;
  }
  return applyWeekendHolidayObservance(floatingCalendarDate, timeZone);
}

/**
 * All Reema observed holiday dates in `year` (in `timeZone`), de-duplicated, sorted.
 */
export function getObservedHolidayDatesForYear(
  year: number,
  timeZone: string
): DateTime[] {
  const seenIsoDateKeys = new Set<string>();
  const uniqueObservedDates: DateTime[] = [];
  const rules = REEMA_OBSERVED_HOLIDAYS;
  for (const rule of rules) {
    const observedDate = getObservedHolidayDateForYear(year, rule, timeZone);
    const isoDateKey = observedDate?.toISODate();
    if (observedDate && isoDateKey && !seenIsoDateKeys.has(isoDateKey)) {
      seenIsoDateKeys.add(isoDateKey);
      uniqueObservedDates.push(observedDate);
    }
  }
  return uniqueObservedDates.sort((a, b) => a.toMillis() - b.toMillis());
}

/**
 * Whether `date` falls on a Reema observed company holiday.
 * Checks calendar years `year - 1`, `year`, and `year + 1` in `timeZone` so boundary cases
 * (e.g. New Year's Day observed on prior Dec 31) are covered.
 */
export function isDateOnObservedHoliday(
  date: DateTime,
  timeZone: string
): boolean {
  const rules = REEMA_OBSERVED_HOLIDAYS;
  const dateInZone = date.setZone(timeZone).startOf('day');
  const candidateIsoKey = dateInZone.toISODate();
  if (!candidateIsoKey) {
    return false;
  }
  const calendarYear = dateInZone.year;
  for (const yearOffset of [-1, 0, 1] as const) {
    for (const rule of rules) {
      const observedDate = getObservedHolidayDateForYear(
        calendarYear + yearOffset,
        rule,
        timeZone
      );
      if (observedDate?.toISODate() === candidateIsoKey) {
        return true;
      }
    }
  }
  return false;
}

function isWeekendLuxonWeekday(weekday: number): boolean {
  return weekday === 6 || weekday === 7;
}

/**
 * Whether `dateOrIso` is a business day in `timeZone`: Monday–Friday and not a Reema
 * observed holiday. Accepts Luxon `DateTime` or `yyyy-MM-dd`. Invalid ISO strings
 * return false. For “run every business day” jobs instead of month-window helpers like
 * `isIsoDateInFirstNBusinessDaysOfYearMo`.
 */
export function isBusinessDay(
  dateOrIso: DateTime | string,
  timeZone: string = 'UTC'
): boolean {
  const dateInZone =
    typeof dateOrIso === 'string'
      ? DateTime.fromISO(dateOrIso, { zone: timeZone }).startOf('day')
      : dateOrIso.setZone(timeZone).startOf('day');
  if (!dateInZone.isValid) {
    return false;
  }
  if (isWeekendLuxonWeekday(dateInZone.weekday)) {
    return false;
  }
  return !isDateOnObservedHoliday(dateInZone, timeZone);
}

/**
 * Yields ISO dates (`yyyy-MM-dd`) for each business day in `year`/`month` in order
 * (weekends and Reema holidays skipped). Stops at the end of the month.
 */
function* eachBusinessDayIsoDateInMonth(
  year: number,
  month: number,
  timeZone: string
): Generator<string> {
  let cursor = DateTime.fromObject(
    { year, month, day: 1 },
    { zone: timeZone }
  ).startOf('day');
  if (!cursor.isValid) {
    return;
  }
  while (cursor.month === month) {
    const iso = cursor.toISODate();
    if (iso && isBusinessDay(cursor, timeZone)) {
      yield iso;
    }
    cursor = cursor.plus({ days: 1 });
  }
}

/**
 * ISO calendar dates (`yyyy-MM-dd`) for `count` consecutive business days in a month.
 * Excludes weekends and Reema observed holidays in `timeZone`.
 *
 * @param year Calendar year (e.g. 2026).
 * @param month Calendar month (1–12, Luxon convention).
 * @param timeZone IANA time zone id for interpreting the month boundaries.
 * @param count How many consecutive business days to return.
 * @param startBusinessDayRank 1-based index of the first business day to include
 *   (1 = first business day of the month, 2 = second, …). Default `1`.
 * @example Ranks 2–5 only: `getFirstNBusinessDayIsoDatesInMonth(..., 4, 2)`.
 */
export function getFirstNBusinessDayIsoDatesInMonth(
  year: number,
  month: number,
  timeZone = 'UTC',
  count: number,
  startBusinessDayRank = 1
): string[] {
  if (startBusinessDayRank < 1 || count < 1) {
    return [];
  }
  const isoDates: string[] = [];
  let businessDayRank = 0;
  for (const iso of eachBusinessDayIsoDateInMonth(year, month, timeZone)) {
    businessDayRank++;
    if (businessDayRank < startBusinessDayRank) {
      continue;
    }
    isoDates.push(iso);
    if (isoDates.length >= count) {
      break;
    }
  }
  return isoDates;
}

/** Split `yyyyMM` (e.g. 202603) into calendar year and month. */
export function yearAndMonthFromYearMo(yearMo: number): {
  year: number;
  month: number;
} {
  return { year: Math.floor(yearMo / 100), month: yearMo % 100 };
}

/**
 * Whether `isoDate` (`yyyy-MM-dd`) falls on one of `businessDayCount` consecutive business days
 * starting at `startBusinessDayRank` (1-based) in the month for `yearMo`.
 *
 * @param isoDate Calendar date as `yyyy-MM-dd`.
 * @param yearMo Month bucket as `yyyyMM` (e.g. 202603).
 * @param timeZone IANA time zone id for interpreting the month boundaries.
 * @param businessDayCount Number of consecutive business days in the window. Default `5`.
 * @param startBusinessDayRank 1 = first business day of the month; use `2` to match only from
 *   the 2nd business day onward (e.g. ranks 2–5 with `businessDayCount: 4`). Default `1`.
 */
export function isIsoDateInFirstNBusinessDaysOfYearMo(
  isoDate: string,
  yearMo: number,
  timeZone: string = 'UTC',
  businessDayCount = 5,
  startBusinessDayRank = 1
): boolean {
  if (startBusinessDayRank < 1 || businessDayCount < 1) {
    return false;
  }
  const { year, month } = yearAndMonthFromYearMo(yearMo);
  const endBusinessDayRank = startBusinessDayRank + businessDayCount - 1;
  let businessDayRank = 0;
  for (const iso of eachBusinessDayIsoDateInMonth(year, month, timeZone)) {
    businessDayRank++;
    if (businessDayRank < startBusinessDayRank) {
      continue;
    }
    if (businessDayRank > endBusinessDayRank) {
      return false;
    }
    if (iso === isoDate) {
      return true;
    }
  }
  return false;
}
